import axios from "axios";
import { EXCLUDED_FAMILY_CLAN_IDS } from "../../util/familyClanRankingConfig";
import {
  CLEAR_FAMILY_CLAN,
  GET_FAMILY_CLANS_FAILURE,
  GET_FAMILY_CLANS_REQUEST,
  GET_FAMILY_CLANS_SUCCESS,
  GET_FAMILY_CLAN_ANALYTICS_FAILURE,
  GET_FAMILY_CLAN_ANALYTICS_REQUEST,
  GET_FAMILY_CLAN_ANALYTICS_SUCCESS,
  GET_FAMILY_CLAN_FAILURE,
  GET_FAMILY_CLAN_HISTORY_FAILURE,
  GET_FAMILY_CLAN_HISTORY_REQUEST,
  GET_FAMILY_CLAN_HISTORY_SUCCESS,
  GET_FAMILY_CLAN_RANKING_FAILURE,
  GET_FAMILY_CLAN_RANKING_REQUEST,
  GET_FAMILY_CLAN_RANKING_SUCCESS,
  GET_FAMILY_CLAN_REQUEST,
  GET_FAMILY_CLAN_SUCCESS,
} from "./types";

const getErrorMessage = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

// Admin-only listing. It carries the cashout/recharge reporting columns, so it
// is a different endpoint from the `familyClan` listing the mobile clients use.
export const getAdminFamilyClans =
  ({ period, startDate, endDate } = {}) =>
  async (dispatch) => {
    dispatch({ type: GET_FAMILY_CLANS_REQUEST });
    try {
      const limit = 100;
      let start = 0;
      let total = Infinity;
      let familyClans = [];
      let periodMeta = null;

      // The API is offset-paginated (with a maximum page size of 100). This
      // screen applies its own table pagination and client-side search, so load
      // every API page before handing the result to the table.
      while (start < total) {
        const response = await axios.get("familyClan/admin/overview", {
          params: {
            start,
            limit,
            ...(period ? { period } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
          },
        });
        if (!response.data.status) throw new Error(response.data.message);

        const page = response.data.data || [];
        familyClans = familyClans.concat(page);
        total = response.data.pagination?.total ?? familyClans.length;
        periodMeta = response.data.period ?? periodMeta;

        if (!page.length) break;
        start += page.length;
      }

      dispatch({
        type: GET_FAMILY_CLANS_SUCCESS,
        payload: {
          familyClans: familyClans.filter(
            (clan, index, clans) =>
              clans.findIndex((candidate) => candidate._id === clan._id) ===
              index,
          ),
          period: periodMeta,
        },
      });
    } catch (error) {
      dispatch({
        type: GET_FAMILY_CLANS_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  };

export const getFamilyClan = (id, sort) => async (dispatch) => {
  dispatch({ type: GET_FAMILY_CLAN_REQUEST });
  try {
    const response = await axios.get(`familyClan/${id}`, {
      params: sort ? { sort } : undefined,
    });
    if (!response.data.status) throw new Error(response.data.message);
    dispatch({ type: GET_FAMILY_CLAN_SUCCESS, payload: response.data.data });
  } catch (error) {
    dispatch({ type: GET_FAMILY_CLAN_FAILURE, payload: getErrorMessage(error) });
  }
};

export const getFamilyClanAnalytics =
  (id, month, year) => async (dispatch) => {
    dispatch({ type: GET_FAMILY_CLAN_ANALYTICS_REQUEST });
    try {
      const response = await axios.get(`familyClan/${id}/contribution-summary`, {
        params: { month, year },
      });
      if (!response.data.status) throw new Error(response.data.message);
      dispatch({
        type: GET_FAMILY_CLAN_ANALYTICS_SUCCESS,
        payload: response.data.data,
      });
    } catch (error) {
      dispatch({
        type: GET_FAMILY_CLAN_ANALYTICS_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  };

export const getFamilyClanHistory = (id) => async (dispatch) => {
  dispatch({ type: GET_FAMILY_CLAN_HISTORY_REQUEST });
  try {
    const response = await axios.get(`familyClan/${id}/history`);
    if (!response.data.status) throw new Error(response.data.message);
    dispatch({
      type: GET_FAMILY_CLAN_HISTORY_SUCCESS,
      payload: response.data.data,
    });
  } catch (error) {
    dispatch({
      type: GET_FAMILY_CLAN_HISTORY_FAILURE,
      payload: getErrorMessage(error),
    });
  }
};

// The endpoint defaults to the top 20 clans per page (max 100 per page), so
// this loops through every page rather than accepting the first one — the
// leaderboard page shows the full ranking, not just a top-N cutoff.
export const getFamilyClanRanking =
  ({ period, startDate, endDate } = {}) =>
  async (dispatch) => {
    dispatch({ type: GET_FAMILY_CLAN_RANKING_REQUEST });
    try {
      const limit = 100;
      let start = 0;
      let total = Infinity;
      let ranking = [];

      while (start < total) {
        const response = await axios.get("familyClan/ranking/leaderboard", {
          params: {
            start,
            limit,
            ...(period ? { period } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
          },
        });
        if (!response.data.status) throw new Error(response.data.message);

        const page = response.data.data || [];
        ranking = ranking.concat(page);
        total = response.data.pagination?.total ?? ranking.length;

        if (!page.length) break;
        start += page.length;
      }

      ranking = ranking.filter(
        (entry) =>
          !EXCLUDED_FAMILY_CLAN_IDS.includes(entry.familyClan?.clanId),
      );

      dispatch({ type: GET_FAMILY_CLAN_RANKING_SUCCESS, payload: ranking });
    } catch (error) {
      dispatch({
        type: GET_FAMILY_CLAN_RANKING_FAILURE,
        payload: getErrorMessage(error),
      });
    }
  };

export const clearFamilyClan = () => ({ type: CLEAR_FAMILY_CLAN });
